/**
 * Industry News ranking utilities — sort and filter by vector relevance scores.
 */

import { RSSFeedItem } from '../services/usGovernmentIntegrations';

export type IndustryNewsSortMode = 'relevance' | 'date';

export interface ScoredNewsArticle extends RSSFeedItem {
  relevanceScore: number;
  /** True while vector ranking has not returned a score for this article yet. */
  relevanceScorePending?: boolean;
  matchedKeywords?: string[];
}

type RankApiResultRow = {
  article_id?: string;
  articleId?: string;
  relevance_score?: number;
  relevanceScore?: number;
  matched_keywords?: string[];
  matchedKeywords?: string[];
};

function resolveArticleId(row: RankApiResultRow): string | undefined {
  return row.article_id ?? row.articleId;
}

function resolveRelevanceScore(row: RankApiResultRow): number {
  const raw = row.relevance_score ?? row.relevanceScore;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
}

function resolveMatchedKeywords(row: RankApiResultRow): string[] {
  return row.matched_keywords ?? row.matchedKeywords ?? [];
}

export function buildScoreMap(
  results: Array<RankApiResultRow>
): Map<string, { score: number; matchedKeywords: string[] }> {
  const map = new Map<string, { score: number; matchedKeywords: string[] }>();
  for (const row of results) {
    const articleId = resolveArticleId(row);
    if (!articleId) {
      continue;
    }
    map.set(articleId, {
      score: resolveRelevanceScore(row),
      matchedKeywords: resolveMatchedKeywords(row),
    });
  }
  return map;
}

function articleTimestamp(pubDate: string): number {
  const timestamp = new Date(pubDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function mergeScoreMaps(
  base: Map<string, { score: number; matchedKeywords: string[] }>,
  incoming: Map<string, { score: number; matchedKeywords: string[] }>
): Map<string, { score: number; matchedKeywords: string[] }> {
  const merged = new Map(base);
  for (const [articleId, entry] of incoming) {
    merged.set(articleId, entry);
  }
  return merged;
}

export function attachRelevanceScores(
  articles: RSSFeedItem[],
  scoreMap: Map<string, { score: number; matchedKeywords: string[] }>,
  pendingArticleIds?: ReadonlySet<string>
): ScoredNewsArticle[] {
  return articles.map((article) => {
    const entry = scoreMap.get(article.id);
    const isPending = pendingArticleIds?.has(article.id) ?? false;
    return {
      ...article,
      relevanceScore: entry?.score ?? 0,
      relevanceScorePending: isPending && entry === undefined,
      matchedKeywords: entry?.matchedKeywords ?? [],
    };
  });
}

export function filterBySimilarityThreshold(
  articles: ScoredNewsArticle[],
  threshold: number
): ScoredNewsArticle[] {
  return articles.filter((article) => article.relevanceScore >= threshold);
}

export function sortIndustryNewsArticles(
  articles: ScoredNewsArticle[],
  sortMode: IndustryNewsSortMode
): ScoredNewsArticle[] {
  const copy = [...articles];
  if (sortMode === 'date') {
    copy.sort((a, b) => articleTimestamp(b.pubDate) - articleTimestamp(a.pubDate));
    return copy;
  }
  copy.sort((a, b) => {
    const aPending = a.relevanceScorePending === true;
    const bPending = b.relevanceScorePending === true;
    if (aPending && bPending) {
      return articleTimestamp(b.pubDate) - articleTimestamp(a.pubDate);
    }
    if (aPending) {
      return 1;
    }
    if (bPending) {
      return -1;
    }
    const scoreDiff = b.relevanceScore - a.relevanceScore;
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    return articleTimestamp(b.pubDate) - articleTimestamp(a.pubDate);
  });
  return copy;
}

export function countAboveSimilarityThreshold(
  articles: ScoredNewsArticle[],
  threshold: number
): number {
  return articles.filter((article) => article.relevanceScore >= threshold).length;
}

/** Above-threshold articles first, then below — each group sorted by sortMode. */
export function sortWithThresholdPriority(
  articles: ScoredNewsArticle[],
  threshold: number,
  sortMode: IndustryNewsSortMode
): ScoredNewsArticle[] {
  const above = articles.filter((article) => article.relevanceScore >= threshold);
  const below = articles.filter((article) => article.relevanceScore < threshold);
  return [
    ...sortIndustryNewsArticles(above, sortMode),
    ...sortIndustryNewsArticles(below, sortMode),
  ];
}

export function rankAndFilterIndustryNews(
  articles: RSSFeedItem[],
  scoreMap: Map<string, { score: number; matchedKeywords: string[] }>,
  options: {
    sortMode: IndustryNewsSortMode;
    threshold: number;
    applyThreshold: boolean;
    prioritizeAboveThreshold?: boolean;
  }
): ScoredNewsArticle[] {
  return rankAndFilterIndustryNewsWithPending(articles, scoreMap, undefined, options);
}

export function rankAndFilterIndustryNewsWithPending(
  articles: RSSFeedItem[],
  scoreMap: Map<string, { score: number; matchedKeywords: string[] }>,
  pendingArticleIds: ReadonlySet<string> | undefined,
  options: {
    sortMode: IndustryNewsSortMode;
    threshold: number;
    applyThreshold: boolean;
    prioritizeAboveThreshold?: boolean;
  }
): ScoredNewsArticle[] {
  const scored = attachRelevanceScores(articles, scoreMap, pendingArticleIds);

  if (options.applyThreshold) {
    const filtered = filterBySimilarityThreshold(scored, options.threshold);
    return sortIndustryNewsArticles(filtered, options.sortMode);
  }

  if (options.prioritizeAboveThreshold) {
    return sortWithThresholdPriority(scored, options.threshold, options.sortMode);
  }

  return sortIndustryNewsArticles(scored, options.sortMode);
}
